import { Box, CircularProgress } from "@mui/material";
import { useMemo } from "react";
import type { MilkQualityPerFarmerPeriodData } from "../hooks/useInsightsDashboardQueries";
import { SCC_THRESHOLDS, TBC_THRESHOLDS } from "../milkQualityThresholds";
import StatTile from "./StatTile";

function getLatestPerFarmer(
  rows: MilkQualityPerFarmerPeriodData[]
): Map<string, MilkQualityPerFarmerPeriodData> {
  const latest = new Map<string, MilkQualityPerFarmerPeriodData>();
  for (const r of rows) {
    const uid = r["MilkQuality.userGuid"];
    const existing = latest.get(uid);
    if (
      !existing ||
      r["MilkQuality.periodStart"] > existing["MilkQuality.periodStart"]
    ) {
      latest.set(uid, r);
    }
  }
  return latest;
}

export type AlertMetric = "scc" | "tbc" | "antibiotic";

const ALL_METRICS: AlertMetric[] = ["scc", "tbc", "antibiotic"];

interface MilkQualityAlertCardProps {
  data: MilkQualityPerFarmerPeriodData[] | undefined;
  isLoading: boolean;
  /** Which alert chips to render. Defaults to all three. */
  metrics?: AlertMetric[];
}

export default function MilkQualityAlertCard({
  data,
  isLoading,
  metrics = ALL_METRICS,
}: MilkQualityAlertCardProps) {
  const alerts = useMemo(() => {
    if (!data) return { scc: 0, tbc: 0, antibiotic: 0 };
    const latestMap = getLatestPerFarmer(data);
    let scc = 0;
    let tbc = 0;
    let antibiotic = 0;
    for (const r of latestMap.values()) {
      const sccVal = Number(r["MilkQuality.somaticCellCount"]);
      const tbcVal = Number(r["MilkQuality.totalBacterialCount"]);
      const abTest = r["MilkQuality.antibioticTest"];
      if (sccVal >= SCC_THRESHOLDS.warning) scc++;
      if (tbcVal >= TBC_THRESHOLDS.warning) tbc++;
      if (abTest && abTest.toLowerCase() !== "pass") antibiotic++;
    }
    return { scc, tbc, antibiotic };
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const tiles: Record<AlertMetric, { count: number; label: string }> = {
    scc: { count: alerts.scc, label: "above SCC limit (400k)" },
    tbc: { count: alerts.tbc, label: "above TBC limit (100k)" },
    antibiotic: { count: alerts.antibiotic, label: "failed antibiotic tests" },
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
      {metrics.map((m) => (
        <StatTile
          key={m}
          label={tiles[m].label}
          value={String(tiles[m].count)}
          alert
          active={tiles[m].count > 0}
          maxWidth={320}
        />
      ))}
    </Box>
  );
}
